import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DirectionsCar as CarIcon } from '@mui/icons-material';
import { keyframes } from '@mui/system';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../../services/api';

const fadeInUp = keyframes`
  0% { opacity: 0; transform: translateY(16px); }
  100% { opacity: 1; transform: translateY(0); }
`;

export default function Login() {
  const [loginValue, setLoginValue] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);
  const [backgroundUrl, setBackgroundUrl] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const cacheBust = (url) => (url ? `${url}?t=${Date.now()}` : '');

  useEffect(() => {
    let active = true;

    const checkSetup = async () => {
      try {
        const response = await api.get('/auth/check-setup');
        if (response.data.needsSetup) {
          navigate('/setup');
        }
      } catch (error) {
        console.error('Erro ao verificar setup:', error);
      } finally {
        if (active) {
          setCheckingSetup(false);
        }
      }
    };

    const loadBackground = async () => {
      try {
        const response = await api.get('/branding/login-background');
        if (active) {
          setBackgroundUrl(cacheBust(response.data.url || ''));
        }
      } catch (error) {
        console.error('Erro ao carregar background:', error);
      }
    };

    checkSetup();
    loadBackground();

    return () => {
      active = false;
    };
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    setLoading(true);

    const result = await login(loginValue.trim().toLowerCase(), senha);

    if (result.success) {
      toast.success('Login realizado com sucesso!');
      navigate('/');
    } else {
      setErro(result.error);
      toast.error(result.error);
    }

    setLoading(false);
  };

  if (checkingSetup) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  const backgroundImage = backgroundUrl
    ? `linear-gradient(120deg, rgba(8, 23, 33, 0.8), rgba(11, 38, 52, 0.65)), url(${backgroundUrl})`
    : 'radial-gradient(circle at 10% 20%, rgba(0, 70, 87, 0.35), transparent 45%), linear-gradient(135deg, #0b1f2a, #102a3a 55%, #0b1f2a)';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 460px' },
        backgroundImage,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        color: 'common.white',
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 6,
        }}
      >
        <Box sx={{ animation: `${fadeInUp} 600ms ease` }}>
          <Typography variant="overline" sx={{ letterSpacing: 3, opacity: 0.7 }}>
            BaseComm Suite
          </Typography>
          <Typography variant="h2" sx={{ fontWeight: 600, mt: 2 }}>
            Gestão total da sua operação, com visibilidade, controle e performance.
          </Typography>
          <Typography sx={{ mt: 2, maxWidth: 420, opacity: 0.8 }}>
            Tudo em um só lugar: vendas, clientes, estoque e financeiro organizados em um painel simples e confiável.
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ opacity: 0.6 }}>
          Tecnologia que impulsiona revendas em evolução.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, md: 5 },
          backdropFilter: { xs: 'blur(0px)', md: 'blur(6px)' },
        }}
      >
        <Paper
          elevation={8}
          sx={{
            width: '100%',
            maxWidth: 420,
            p: 4,
            borderRadius: 3,
            animation: `${fadeInUp} 500ms ease`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box
              sx={{
                width: 54,
                height: 54,
                borderRadius: 2,
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'common.white',
              }}
            >
              <CarIcon sx={{ fontSize: 30 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Bem-vindo
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use seu login e senha para entrar
              </Typography>
            </Box>
          </Box>

          {erro && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {erro}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="login"
              label="Login"
              name="login"
              placeholder="nome.sobrenome"
              autoComplete="username"
              autoFocus
              value={loginValue}
              onChange={(e) => setLoginValue(e.target.value.toLowerCase())}
              helperText="Ex: anderson.silva"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="senha"
              label="Senha"
              type="password"
              id="senha"
              autoComplete="current-password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3 }}
              disabled={loading}
            >
                {loading ? 'Entrando...' : 'Entrar'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
